import { useState } from 'react';
import { Trash2, RefreshCw, Send, Database } from 'lucide-react';
import { textProviders, imageProviders } from '@/data/aiProviders';
import { Card, CardHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { Tabs } from '@/components/ui/Tabs';
import { Table, TableHead, TableHeaderCell, TableBody } from '@/components/ui/Table';
import { EmptyState } from '@/components/ui/EmptyState';

const tierTone = { fast: 'analytics', balanced: 'management', premium: 'ai' } as const;

/**
 * docs/IMPLEMENTATION_PLAN.md Feature 26, owner half. Provider names/tiers
 * are real (ai/registry.py, ai/images/registry.py) — "configured" status
 * shown here is always "unknown" since it depends on which API keys are
 * set in the bot's actual environment, which this preview has no way to
 * know without the Bridge. Never guessed or faked as active.
 */
export function AiDiagnosticsPage() {
  const [tab, setTab] = useState('providers');

  return (
    <div className="max-w-3xl space-y-6">
      <Alert
        tone="info"
        title="Read-only diagnostics, once connected"
        description="These calls hit the same underlying ai/ module functions the Discord commands already use — no new bot logic needed, only Bridge exposure."
      />

      <Tabs
        tabs={[
          { id: 'providers', label: 'Providers' },
          { id: 'benchmark', label: 'Benchmark' },
          { id: 'cache', label: 'Cache & Health' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'providers' ? (
        <div className="space-y-4">
          <Card>
            <CardHeader title="Text providers" description="Tiered by ai/router.py's fast / balanced / premium escalation order." />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {textProviders.map((p) => (
                <div key={p.name} className="flex items-center justify-between px-3 py-2 rounded-sm border border-border-subtle">
                  <span className="text-body text-text-primary">{p.name}</span>
                  <div className="flex items-center gap-2">
                    {p.tier ? <Badge tone={tierTone[p.tier]}>{p.tier}</Badge> : null}
                    <Badge tone="neutral">Unknown</Badge>
                  </div>
                </div>
              ))}
            </div>
          </Card>
          <Card>
            <CardHeader title="Image providers" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {imageProviders.map((p) => (
                <div key={p.name} className="flex items-center justify-between px-3 py-2 rounded-sm border border-border-subtle">
                  <span className="text-body text-text-primary">{p.name}</span>
                  <Badge tone="neutral">Unknown</Badge>
                </div>
              ))}
            </div>
          </Card>
        </div>
      ) : null}

      {tab === 'benchmark' ? (
        <Card>
          <CardHeader
            title="Provider benchmark leaderboard"
            action={<Button variant="secondary" size="sm" disabled><Send size={14} aria-hidden="true" />Send test prompt</Button>}
          />
          <Table>
            <TableHead>
              <TableHeaderCell>Provider</TableHeaderCell>
              <TableHeaderCell>Avg. latency</TableHeaderCell>
              <TableHeaderCell>Success rate</TableHeaderCell>
              <TableHeaderCell>Requests</TableHeaderCell>
            </TableHead>
            <TableBody />
          </Table>
          <div className="mt-2">
            <EmptyState icon={Database} title="No benchmark data yet" />
          </div>
        </Card>
      ) : null}

      {tab === 'cache' ? (
        <div className="space-y-4">
          <Card>
            <CardHeader
              title="Response cache"
              action={<Button variant="ghost" size="sm" disabled><Trash2 size={14} aria-hidden="true" />Clear cache</Button>}
            />
            <p className="text-small text-text-secondary">Cache size and hit rate appear here once connected.</p>
          </Card>
          <Card>
            <CardHeader
              title="Provider health"
              description="Circuit-breaker status per provider."
              action={<Button variant="ghost" size="sm" disabled><RefreshCw size={14} aria-hidden="true" />Reload health</Button>}
            />
            <p className="text-small text-text-secondary">Health/failure-window data appears here once connected.</p>
          </Card>
        </div>
      ) : null}
    </div>
  );
}
